// @ts-strict-ignore
import appleTouchIcon from '@assets/favicons/apple-touch-icon.png';
import favicon16 from '@assets/favicons/favicon-16x16.png';
import favicon32 from '@assets/favicons/favicon-32x32.png';
import safariPinnedTab from '@assets/favicons/safari-pinned-tab.svg';
import { useUser } from '@dashboard/auth/useUser';
import { type ShopInfoQuery } from '@dashboard/graphql';
import { createContext, type ReactNode, useEffect, useMemo } from 'react';
import Helmet from 'react-helmet';

import { useAnalytics } from '../ProductAnalytics/useAnalytics';
import { extractEmailDomain } from '../ProductAnalytics/utils';

type ShopContext = ShopInfoQuery['shop'];

export const ShopContext = createContext<ShopContext>(undefined);

// NuxNode does not run a Saleor backend; the legacy `shop` GraphQL query was
// retired. We synthesise the shape ShopInfoQuery["shop"] expects from
// window.location + product constants so existing consumers (Helmet titles,
// analytics initialisers, etc.) keep working without an upstream call.
export const ShopProvider = ({ children }: { children: ReactNode }) => {
  const { authenticated, user } = useUser();
  const analytics = useAnalytics();

  const shop = useMemo<ShopContext | undefined>(() => {
    if (!authenticated) {
      return undefined;
    }

    const host = typeof window !== 'undefined' ? window.location.host : '';
    const url = typeof window !== 'undefined' ? window.location.origin : '';

    return {
      __typename: 'Shop',
      name: 'NuxNode VM Console',
      version: '1.0.0',
      defaultWeightUnit: null,
      countries: [],
      defaultCountry: null,
      domain: {
        __typename: 'Domain',
        host,
        url,
      },
      languages: [],
      permissions: [],
    };
  }, [authenticated]);

  useEffect(() => {
    if (shop && user) {
      analytics.initialize({
        domain: shop.domain.host,
        email_domain: extractEmailDomain(user.email),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shop, user]);

  return (
    <>
      <Helmet>
        <link rel="apple-touch-icon" sizes="180x180" href={appleTouchIcon} />
        <link rel="icon" type="image/png" sizes="32x32" href={favicon32} />
        <link rel="icon" type="image/png" sizes="16x16" href={favicon16} />
        <link rel="mask-icon" href={safariPinnedTab} />
      </Helmet>
      <ShopContext.Provider value={shop}>{children}</ShopContext.Provider>
    </>
  );
};
